export type IntakeRowKind = 'case' | 'lead';

export type IntakeFlagFilter =
  | 'all'
  | 'no_contact_2d'
  | 'lor_unconfirmed'
  | 'lor_not_sent'
  | 'lor_confirmed';

/**
 * Where a lead came from. Mirrors `IntakeLeadSource` in
 * backend/src/utils/intakeHome/queryIntakeHome.ts; backend `npm run test-intake-home` fails if the
 * two lists differ.
 */
export type IntakeLeadSource =
  | 'lris_email'
  | 'website_form_email'
  | 'website_checkout'
  | 'checkout_paid_no_case';

export type IntakeRowKindFilter = 'all' | 'case' | 'lead';

/** An existing client sharing the lead's email or phone. Shown for staff to check; never merged. */
export interface IntakeLeadMatchDto {
  userGuid: string;
  name: string;
  reasons: Array<'email' | 'phone'>;
  warnings: string[];
  caseCount: number;
  lastCaseAt: string | null;
}

export interface IntakeLeadRowDetailsDto {
  leadGuid: string;
  status: 'OPEN' | 'CONTACTED';
  statusBy: string | null;
  statusAt: string | null;
  note: string | null;
  county: string | null;
  details: {
    ticketType?: string | null;
    language?: string | null;
    legalIssue?: string | null;
    referredBy?: string | null;
    product?: string | null;
    tier?: string | null;
    courtName?: string | null;
    amountCents?: number | null;
  };
  matches: IntakeLeadMatchDto[];
}

/** One Intake home row, exactly as the backend sends it (`IntakeTrackingRowDto` in queryIntakeHome.ts). */
export interface IntakeTrackingRowDto {
  rowId: string;
  kind: IntakeRowKind;
  ticketId: number | null;
  ticketGuid: string | null;
  matterNumber: string | null;
  ticketNumber: string | null;
  citationDescription: string | null;
  matterType: string | null;
  statusName: string | null;
  userId: number | null;
  userGuid: string | null;
  leadSource: IntakeLeadSource | null;
  leadReferenceId: string | null;
  lead: IntakeLeadRowDetailsDto | null;
  clientName: string;
  clientEmail: string | null;
  clientPhone: string | null;
  courtName: string | null;
  createdAt: string;
  assignedStaffName: string | null;
  assignedStaffSource: 'assigned' | 'last_contact' | null;
  lastContactAt: string | null;
  lastContactBy: string | null;
  lastContactKind: 'call' | 'note' | 'message' | null;
  lastContactCallDirection: 'inbound' | 'outbound' | null;
  lastContactCallOutcome: 'spoke' | 'voicemail' | 'no-answer' | null;
  lastContactSummary: string | null;
  daysSinceLastContact: number;
  flaggedNoContact2PlusDays: boolean;
  lorSent: boolean;
  lorSentAt: string | null;
  lorConfirmedAt: string | null;
  daysSinceLorSent: number | null;
  flaggedLorUnconfirmed: boolean;
}

export interface MissedCallsSummaryDto {
  totalMissedInboundThisWeek: number;
  voicemailInboundThisWeek: number;
  noAnswerInboundThisWeek: number;
}

export interface UnknownNumbersSummaryDto {
  totalCallsThisWeek: number;
  distinctCallersThisWeek: number;
  inboundCalls: number;
  outboundCalls: number;
  spokeCalls: number;
  noAnswerCalls: number;
  voicemailCalls: number;
}

export interface IntakeAgentWeeklySummaryDto {
  agentName: string;
  managerId: number | null;
  callsThisWeek: number;
  outboundCallsThisWeek: number;
  inboundCallsThisWeek: number;
  connectedCallsThisWeek: number;
  spokeCallsThisWeek?: number;
  unmatchedCallsThisWeek: number;
  followUpNotesThisWeek: number;
  messagesSentThisWeek: number;
  distinctCasesTouchedThisWeek: number;
  assignedOpenCaseCount: number;
  flaggedNoContactCount: number;
  flaggedLorUnconfirmedCount: number;
  isUnattributedLine?: boolean;
  hasActivityThisWeek?: boolean;
}

export interface IntakeHomeResponseDto {
  rows: IntakeTrackingRowDto[];
  totalOpenCases: number;
  totalLeads: number;
  paidNoCaseCount?: number;
  flaggedNoContactCount: number;
  flaggedLorUnconfirmedCount: number;
  flaggedLorNotSentCount: number;
  lorConfirmedCount: number;
  staffOptions: string[];
  agentWeeklySummaries: IntakeAgentWeeklySummaryDto[];
  unattributedDialpadCallsThisWeek: number;
  unmatchedCallsThisWeek: number;
  missedCallsSummary?: MissedCallsSummaryDto;
  unknownNumbersSummary?: UnknownNumbersSummaryDto;
  missingDataDisclosures: string[];
}

export interface FetchIntakeHomeOptions {
  staff?: string;
  flag?: IntakeFlagFilter;
  search?: string;
  createdDays?: number;
  rowKind?: IntakeRowKindFilter;
}

export async function fetchIntakeHome(
  options: FetchIntakeHomeOptions = {},
): Promise<IntakeHomeResponseDto> {
  const params = new URLSearchParams();
  if (options.staff && options.staff !== 'all') {
    params.set('staff', options.staff);
  }
  if (options.flag && options.flag !== 'all') {
    params.set('flag', options.flag);
  }
  if (options.search?.trim()) {
    params.set('search', options.search.trim());
  }
  if (typeof options.createdDays === 'number' && options.createdDays > 0) {
    params.set('createdWithinDays', String(options.createdDays));
  }
  if (options.rowKind && options.rowKind !== 'all') {
    params.set('rowKind', options.rowKind);
  }

  const response = await fetch(
    `${process.env.API_HOST || ''}/api/v1/intake-home?${params.toString()}`,
    {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    },
  );

  if (!response.ok) {
    throw new Error(
      `Failed to fetch intake home: ${response.status} ${response.statusText}`,
    );
  }

  return response.json();
}

async function postLeadAction(leadGuid: string, action: 'contacted' | 'dismiss', body: object): Promise<void> {
  const response = await fetch(
    `${process.env.API_HOST || ''}/api/v1/intake-leads/${encodeURIComponent(leadGuid)}/${action}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(body),
    },
  );
  if (!response.ok) {
    const payload = await response.json().catch(() => ({}));
    throw new Error(payload.message || `Could not update the lead (${response.status})`);
  }
}

/** Mark a lead contacted (it stays on the list). Sends nothing to the prospect. */
export function markLeadContacted(leadGuid: string, note?: string): Promise<void> {
  return postLeadAction(leadGuid, 'contacted', { note: note ?? null });
}

/** Take a lead off the Intake home list. Sends nothing to the prospect. */
export function dismissLead(leadGuid: string, reason?: string): Promise<void> {
  return postLeadAction(leadGuid, 'dismiss', { reason: reason ?? null });
}
